import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress'; // For loading state
import therapistStyles from './styles/therapistStyles'; // Import new styles

// Icons
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit'; // For Edit & Forward button
import CancelIcon from '@mui/icons-material/Cancel'; // For Cancel Edit button

const AiGoalProposalCard = ({
  proposal, // The pendingGoalProposal object
  isEditing, // Boolean: isEditingGoalProposal
  editedDescription,
  onDescriptionChange,
  editedType,
  onTypeChange,
  onForwardAsIs, // Handler for "Forward As Is"
  onStartEdit, // Handler for "Edit & Forward" button (to enter edit mode)
  onCancelEdit, // Handler for "Cancel Edit" button
  onSendEdited, // Handler for "Send Edited Goal" button
  onReject, // Handler for "Reject" button
  actionLoading,
  actionError,
  onClearError, // Function to clear the error message
}) => {
  if (!proposal) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ ...therapistStyles.goalProposalCard, flexShrink: 0 }} data-testid="goal-proposal">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#2E7D32', display: 'flex', alignItems: 'center', fontWeight: 600 }}>
          <TrackChangesIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} /> {isEditing ? "Editing AI Proposed Goal" : "AI Proposed Goal"}
        </Typography>
        {!isEditing && (
          <Box>
            <Button size="small" variant="contained" color="primary" sx={{ mr: 1 }} onClick={onForwardAsIs} disabled={actionLoading} data-testid="accept-goal">
              Forward As Is
            </Button>
            <Button size="small" variant="outlined" color="primary" sx={{ mr: 1 }} onClick={onStartEdit} disabled={actionLoading}>
              Edit & Forward
            </Button>
            <Button size="small" color="error" onClick={onReject} disabled={actionLoading}>
              Reject
            </Button>
          </Box>
        )}
      </Box>
      {actionError && <Alert severity="error" sx={{ my: 1 }} onClose={onClearError}>{actionError}</Alert>}
      
      {isEditing ? (
        <Box>
          <TextField
            fullWidth
            label="Goal Description"
            variant="outlined"
            size="small"
            multiline
            rows={3}
            value={editedDescription}
            onChange={onDescriptionChange}
            sx={{ mb: 1 }}
            disabled={actionLoading}
          />
          <FormControl size="small" fullWidth sx={{ mb: 1 }} disabled={actionLoading}>
            <InputLabel>Goal Type</InputLabel>
            <Select
              value={editedType}
              label="Goal Type"
              onChange={onTypeChange}
            >
              <MenuItem value="one_off">One-off Task</MenuItem>
              <MenuItem value="habit">Habit</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt:1 }}>
            <Button size="small" onClick={onCancelEdit} startIcon={<CancelIcon />} disabled={actionLoading}>
              Cancel Edit
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              color="primary" 
              onClick={onSendEdited} 
              disabled={actionLoading || !editedDescription?.trim()} 
              startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            >
              Send Edited Goal
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {proposal.originalAiText && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'warning.darker', fontStyle: 'italic', mb: 1 }}>
              AI says: "{proposal.originalAiText}"
            </Typography>
          )}
          <Typography variant="body1" sx={{ color: 'warning.darker', fontWeight: 'bold' }}>
            Goal: {proposal.goalDetails?.description}
          </Typography>
          <Typography variant="caption" sx={{ color: 'warning.darker' }}>
            Type: {proposal.goalDetails?.goalType === 'one_off' ? 'One-off Task' : 'Habit'}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default AiGoalProposalCard;
